import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResizeObserver } from '@makemydeal/dr-shared-ui-utils';

import { Action } from '@interstate/components/Action';
import { LinkChip } from '@interstate/components/Chip';
import { Button } from '@interstate/components/Button';
import { FilterChip } from '@interstate/components/Chip';
import { FunnelIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { changeVehicleSelectors } from '../../store';
import type { AppliedFilterTag } from '../../types';
import { getSelectedFilterTags } from '../../utils/appliedFilterTags';
import { StyledVehicleFilterTags } from './VehicleAppliedFilterTags.style';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';
import { getFilterOptionLabel } from '../../utils';

export interface VehicleAppliedFilterTagsProps {
    isMobileView?: boolean;
}

export const MIN_TRUNCATED_TAG_WIDTH = 260;
export const MIN_TRUNCATED_MULTIPLE_TAGS_WIDTH = 310;
export const TRUNCATED_WIDTH = 80;

const VehicleAppliedFilterTags = ({ isMobileView = false }: VehicleAppliedFilterTagsProps) => {
    const theme = useInterstateTheme();
    const isFiltersInitialized = useSelector(changeVehicleSelectors.getIsFiltersInitialized);
    const dealerships = useSelector(changeVehicleSelectors.getDealerships);
    const currentDealer = useSelector(changeVehicleSelectors.getCurrentDealer);
    const showFiltersButton = isMobileView && isFiltersInitialized;

    const { selectedFilters, removeFilters, clearAllFilters, setFilterSlideOutOpen } = useVehicleSearchFiltersContext();

    const appliedFilterTags: AppliedFilterTag[] = useMemo(() => getSelectedFilterTags(selectedFilters), [selectedFilters]);

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [maxChipsToShow, setMaxChipsToShow] = useState(0);
    const [truncateFirst, setTruncateFirst] = useState(false);

    const availableWidth = useMemo(() => {
        // Rough estimate of non-chip elements' width
        const occupiedWidth = 220;

        return containerWidth - occupiedWidth;
    }, [containerWidth]);

    const chipsCanFitAmount = useMemo(() => {
        if (!isMobileView) {
            return appliedFilterTags.length;
        }

        const getApproximateTagWidth = (tagLabel: string) => {
            const padding = 8;
            const emptyTagWidth = 38;
            const averageCharWidth = 9;
            return emptyTagWidth + tagLabel.length * averageCharWidth + padding;
        };

        let totalWidth = 0;
        let visibleCount = 0;

        for (const tag of appliedFilterTags) {
            totalWidth += getApproximateTagWidth(tag.tagLabel);
            if (totalWidth > availableWidth) {
                break;
            }
            visibleCount++;
        }

        return visibleCount;
    }, [appliedFilterTags, availableWidth]);

    useEffect(() => {
        let showFirstTruncatedTag = false;

        if (chipsCanFitAmount === 0 && appliedFilterTags.length > 0) {
            const showHiddenChipsCount = appliedFilterTags.length > 1;
            const allowedWidth = showHiddenChipsCount ? MIN_TRUNCATED_MULTIPLE_TAGS_WIDTH : MIN_TRUNCATED_TAG_WIDTH;

            showFirstTruncatedTag = containerWidth > allowedWidth;
        }

        setMaxChipsToShow(showFirstTruncatedTag ? 1 : chipsCanFitAmount);
        setTruncateFirst(showFirstTruncatedTag);
    }, [chipsCanFitAmount, appliedFilterTags, containerWidth]);

    const hiddenChipsCount = appliedFilterTags.length - maxChipsToShow;

    useResizeObserver(containerRef, (entry) => {
        const width = entry.borderBoxSize[0].inlineSize;
        setContainerWidth(width);
    });

    if (appliedFilterTags.length === 0 && !showFiltersButton) {
        return null;
    }

    return (
        <StyledVehicleFilterTags ref={containerRef} data-testid="vehicle-applied-filter-tags">
            {showFiltersButton && (
                <Button
                    buttonStyle="secondary"
                    startIcon={<FunnelIcon />}
                    data-testid="open-filters-slide-out-button"
                    size="small"
                    onClick={() => setFilterSlideOutOpen(true)}
                    sx={{ marginRight: theme.tokens.BaseSpace20 }}
                >
                    Filters
                </Button>
            )}
            {appliedFilterTags.slice(0, maxChipsToShow).map(({ appliedFilters, tagLabel, groupId }) => (
                <FilterChip
                    key={`${groupId}_${tagLabel}`}
                    data-testid="vehicle-applied-filter-tags-chip"
                    label={getFilterOptionLabel(tagLabel, groupId, dealerships, currentDealer)}
                    onClick={() => removeFilters(appliedFilters)}
                    sx={{ maxWidth: truncateFirst ? `${TRUNCATED_WIDTH}px` : 'unset' }}
                />
            ))}
            {hiddenChipsCount > 0 && (
                <LinkChip label={`+ ${hiddenChipsCount}`} data-testid="hidden-applied-filters-count-label"></LinkChip>
            )}
            {appliedFilterTags.length > 0 && (
                <Action
                    data-testid="vehicle-applied-filter-tags-clear-all"
                    size="sm"
                    sx={{ margin: `0 ${theme.tokens.ComponentBtnSizeSmallPadding}` }}
                    onClick={clearAllFilters}
                >
                    Clear All
                </Action>
            )}
        </StyledVehicleFilterTags>
    );
};

export default VehicleAppliedFilterTags;
